import MiniSearch from 'minisearch';
import { BASE_URL } from './config';

const API_BOOKS = 'https://api.buku.cloudapp.web.id/katalog/books';
const API_FACETS = 'https://api.buku.cloudapp.web.id/katalog/facets';
const API_DETAIL = `${BASE_URL}/api/catalogue/getDetails`;

/**
 * Cache pencarian.
 *
 * Prinsipnya: user TIDAK PERNAH perlu menghapus localStorage.
 * - Satu key saja (`minisearch_cache`) berisi versi + tanda tangan data + isi katalog.
 * - Data cache dipakai instan (stale-while-revalidate), lalu di belakang layar dicek
 *   ke API. Kalau isi katalog berubah, index dibangun ulang dan komponen diberi tahu
 *   lewat subscribeMiniSearch() tanpa reload halaman.
 * - Key versi lama dibersihkan otomatis supaya tidak menumpuk.
 */
const CACHE_VERSION = 4;
const CACHE_KEY = 'minisearch_cache';
const CHECK_TS_KEY = 'minisearch_last_check';
const LEGACY_KEYS = ['minisearch_books_cache', 'minisearch_books_ts', 'minisearch_facets_cache'];
const SLUG_MAP_KEY = 'minisearch_slug_map';
const RECENT_QUERY_KEY = 'spotlight_recent_queries';
const RECENT_BOOK_KEY = 'spotlight_recent_books';

// Cek pembaruan paling sering tiap 10 menit per sesi (hemat request, tetap segar).
const REVALIDATE_INTERVAL_MS = 10 * 60 * 1000;
const MAX_RECENT = 6;

// ── Label tampilan ────────────────────────────────────────────────────────────
export const FILE_TYPE_LABEL = {
    pdf: 'PDF',
    audio: 'Audio',
    interactive: 'Interaktif',
};

export const JENIS_BUKU_LABEL = {
    kurmer: 'Kurikulum Merdeka',
    k13: 'Kurikulum 2013',
    nonteks: 'Non-Teks',
};

export const JENJANG_LABEL = {
    A: 'Jenjang A',
    B: 'Jenjang B',
    C: 'Jenjang C',
    D: 'Jenjang D',
    E: 'Jenjang E',
    PAUD: 'PAUD',
    SD: 'SD',
    SMP: 'SMP',
    SMA: 'SMA',
    SMK: 'SMK',
};

export const getJenjangLabel = (jenjang) => JENJANG_LABEL[jenjang] || jenjang || '';

// ── localStorage aman (mode privat / kuota penuh tidak boleh bikin crash) ─────
let storageEnabled = true;

function safeGet(key) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSet(key, value) {
    if (!storageEnabled) return false;
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch {
        // Kuota penuh / storage diblokir: matikan cache untuk sesi ini,
        // pencarian tetap jalan dari memori.
        storageEnabled = false;
        try {
            window.localStorage.removeItem(key);
        } catch {
            /* abaikan */
        }
        return false;
    }
}

function safeRemove(key) {
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* abaikan */
    }
}

// ── State modul (singleton) ──────────────────────────────────────────────────
let miniSearchInstance = null;
let booksData = [];
let facetsData = null;
let cacheSignature = '';
let loadPromise = null;
let revalidatePromise = null;
let loadError = null;
let searchVersion = 0;
const listeners = new Set();

export function subscribeMiniSearch(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getSearchVersion() {
    return searchVersion;
}

function notify() {
    searchVersion += 1;
    listeners.forEach((listener) => {
        try {
            listener(searchVersion);
        } catch {
            /* listener rusak tidak boleh menghentikan yang lain */
        }
    });
}

// ── Persiapan data ───────────────────────────────────────────────────────────
/**
 * Facets dari API berbentuk { mapel: { 'Matematika': ['id', ...] }, kelas: {...}, tag: {...} }.
 * Diubah jadi peta id -> label supaya tiap buku bisa diberi konteks mapel/kelas/tag.
 */
function buildFacetMaps(facets) {
    const maps = { mapel: {}, kelas: {}, tag: {} };

    const fill = (target, group) => {
        if (!group || typeof group !== 'object' || Array.isArray(group)) return;
        Object.entries(group).forEach(([label, ids]) => {
            if (!Array.isArray(ids)) return;
            ids.forEach((id) => {
                const key = String(id);
                if (!target[key]) target[key] = label;
            });
        });
    };

    fill(maps.mapel, facets?.mapel);
    fill(maps.kelas, facets?.kelas);
    fill(maps.tag, facets?.tag);
    return maps;
}

function enrichBooks(books, facets) {
    const maps = buildFacetMaps(facets);
    return books.map((book) => ({
        ...book,
        mapel: book.mapel || maps.mapel[String(book.id)] || '',
        kelas: book.kelas || maps.kelas[String(book.id)] || '',
        tag: book.tag || maps.tag[String(book.id)] || '',
    }));
}

function buildIndex(books, facets) {
    const miniSearch = new MiniSearch({
        idField: 'id',
        fields: ['judul', 'mapel', 'kelas', 'tag', 'jenjang', 'jenis_buku', 'tipe_file'],
        storeFields: ['judul', 'image', 'tipe_file', 'jenis_buku', 'jenjang', 'mapel', 'kelas'],
        searchOptions: {
            prefix: true,
            fuzzy: 0.2,
            boost: { judul: 3, mapel: 2 },
            combineWith: 'AND',
        },
    });

    miniSearch.addAll(enrichBooks(books, facets));
    return miniSearch;
}

/** Tanda tangan isi katalog: berubah kalau jumlah/judul/cover berubah. */
function signatureOf(books) {
    let hash = 2166136261;
    for (let i = 0; i < books.length; i += 1) {
        const book = books[i];
        const text = `${book.id}:${book.judul || ''}:${book.image || ''}`;
        for (let j = 0; j < text.length; j += 1) {
            hash ^= text.charCodeAt(j);
            hash = Math.imul(hash, 16777619);
        }
    }
    return `${books.length}-${(hash >>> 0).toString(36)}`;
}

function persistCache(books, facets, signature) {
    safeSet(
        CACHE_KEY,
        JSON.stringify({
            v: CACHE_VERSION,
            sig: signature,
            savedAt: Date.now(),
            books,
            facets,
        })
    );
}

function readCache() {
    LEGACY_KEYS.forEach(safeRemove);

    const raw = safeGet(CACHE_KEY);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== CACHE_VERSION) {
            safeRemove(CACHE_KEY);
            return null;
        }
        if (!Array.isArray(parsed.books) || parsed.books.length === 0) {
            safeRemove(CACHE_KEY);
            return null;
        }
        return parsed;
    } catch {
        safeRemove(CACHE_KEY);
        return null;
    }
}

async function fetchJSON(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Gagal memuat ${url} (${response.status})`);
    return response.json();
}

async function fetchFreshData() {
    const [books, facets] = await Promise.all([
        fetchJSON(API_BOOKS),
        fetchJSON(API_FACETS).catch(() => null),
    ]);

    if (!Array.isArray(books) || books.length === 0) {
        throw new Error('Data katalog kosong');
    }
    return { books, facets };
}

function applyData(books, facets, signature) {
    booksData = books;
    facetsData = facets;
    cacheSignature = signature;
    miniSearchInstance = buildIndex(books, facets);
    loadError = null;
}

function snapshot() {
    return {
        miniSearch: miniSearchInstance,
        books: booksData,
        facets: facetsData,
        version: searchVersion,
        error: loadError,
    };
}

/**
 * Cek data terbaru di belakang layar (maks. sekali per 10 menit).
 * Mengembalikan true kalau index dibangun ulang.
 */
export function revalidateMiniSearch({ force = false } = {}) {
    if (revalidatePromise) return revalidatePromise;

    if (!force) {
        const lastCheck = parseInt(safeGet(CHECK_TS_KEY) || '0', 10);
        if (Date.now() - lastCheck < REVALIDATE_INTERVAL_MS) return Promise.resolve(false);
    }

    revalidatePromise = fetchFreshData()
        .then(({ books, facets }) => {
            safeSet(CHECK_TS_KEY, Date.now().toString());

            const signature = signatureOf(books);
            const changed = !miniSearchInstance || signature !== cacheSignature;

            if (!changed) {
                // Isi sama: cukup perbarui facets bila sebelumnya tidak tersimpan.
                if (facets && !facetsData) {
                    facetsData = facets;
                    persistCache(books, facets, signature);
                }
                return false;
            }

            applyData(books, facets, signature);
            persistCache(books, facets, signature);
            notify();
            return true;
        })
        .catch(() => false)
        .finally(() => {
            revalidatePromise = null;
        });

    return revalidatePromise;
}

/**
 * Muat index pencarian (singleton).
 * Cache dipakai lebih dulu, pembaruan data dicek otomatis di belakang layar.
 */
export function loadMiniSearch({ force } = {}) {
    if (miniSearchInstance) {
        revalidateMiniSearch({ force });
        return Promise.resolve(snapshot());
    }
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        if (force) safeRemove(CHECK_TS_KEY);

        const cached = readCache();
        if (cached) {
            const signature = cached.sig || signatureOf(cached.books);
            applyData(cached.books, cached.facets, signature);
            // Jangan blokir UI: cek versi terbaru setelah index siap.
            revalidateMiniSearch({ force });
            return snapshot();
        }

        try {
            const { books, facets } = await fetchFreshData();
            const signature = signatureOf(books);
            applyData(books, facets, signature);
            persistCache(books, facets, signature);
            safeSet(CHECK_TS_KEY, Date.now().toString());
        } catch (error) {
            loadError = error?.message || 'Gagal memuat data pencarian';
            // Biarkan percobaan berikutnya mencoba lagi (mis. jaringan pulih).
            loadPromise = null;
        }

        return snapshot();
    })();

    return loadPromise;
}

/** Paksa ambil ulang data sekarang (dipakai tombol "Coba lagi"). */
export function retryMiniSearch() {
    loadPromise = null;
    safeRemove(CHECK_TS_KEY);
    loadError = null;
    if (miniSearchInstance) {
        return revalidateMiniSearch({ force: true }).then(snapshot);
    }
    return loadMiniSearch({ force: true });
}

/** Hapus cache pencarian (opsional; user umumnya tidak perlu ini). */
export function clearSearchCache() {
    [CACHE_KEY, CHECK_TS_KEY, ...LEGACY_KEYS].forEach(safeRemove);
    cacheSignature = '';
    miniSearchInstance = null;
    booksData = [];
    facetsData = null;
    loadPromise = null;
    notify();
}

// ── Normalisasi kata kunci ───────────────────────────────────────────────────
const STOPWORDS = new Set([
    'buku', 'untuk', 'yang', 'dengan', 'dan', 'atau', 'di', 'ke', 'dari', 'pada',
    'the', 'of', 'a', 'an', 'cari', 'mencari', 'tolong',
]);

// Singkatan yang sering dipakai guru/siswa -> istilah lengkap di data.
const SYNONYMS = {
    mtk: 'matematika',
    mat: 'matematika',
    ipa: 'ilmu pengetahuan alam',
    ipas: 'ilmu pengetahuan alam dan sosial',
    ips: 'ilmu pengetahuan sosial',
    bindo: 'bahasa indonesia',
    bi: 'bahasa inggris',
    big: 'bahasa inggris',
    pjok: 'pendidikan jasmani olahraga dan kesehatan',
    ppkn: 'pendidikan pancasila',
    pkn: 'pendidikan pancasila',
    sbdp: 'seni budaya',
    tik: 'informatika',
    prakarya: 'prakarya dan kewirausahaan',
};

export function normalizeQuery(query) {
    const tokens = String(query || '')
        .toLowerCase()
        .replace(/[^\w\s-]/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .filter((token) => !STOPWORDS.has(token));

    const expanded = [];
    tokens.forEach((token) => {
        const synonym = SYNONYMS[token];
        if (synonym) {
            expanded.push(...synonym.split(' '));
        } else {
            expanded.push(token);
        }
    });

    return expanded.join(' ');
}

// ── Pencarian ────────────────────────────────────────────────────────────────
/**
 * Cari buku.
 * @returns {{ items: object[], total: number }} total = seluruh kecocokan (bukan hanya halaman ini)
 */
export function searchBooks(query, options = {}) {
    const { limit = 8, jenis, jenjang, tipe } = options;

    if (!miniSearchInstance || !String(query || '').trim()) {
        return { items: [], total: 0 };
    }

    const normalized = normalizeQuery(query);
    let matches = normalized ? miniSearchInstance.search(normalized, { combineWith: 'AND' }) : [];

    // AND bisa terlalu ketat ("matematika kelas 4"): longgarkan jadi OR.
    if (matches.length === 0) {
        matches = miniSearchInstance.search(String(query).trim(), { combineWith: 'OR' });
    }

    const filtered = matches.filter((item) => {
        if (jenis && jenis !== 'semua' && item.jenis_buku !== jenis) return false;
        if (jenjang && item.jenjang !== jenjang) return false;
        if (tipe && item.tipe_file !== tipe) return false;
        return true;
    });

    return {
        items: filtered.slice(0, limit),
        total: filtered.length,
    };
}

/** Opsi filter dinamis dari facets (fallback: dari data buku yang termuat). */
export function getFilterOptions() {
    const unique = (values) => Array.from(new Set(values.filter(Boolean)));

    const facetJenjang = facetsData?.jenjang && !Array.isArray(facetsData.jenjang)
        ? Object.keys(facetsData.jenjang)
        : [];
    const facetTipe = facetsData?.tipe_file && !Array.isArray(facetsData.tipe_file)
        ? Object.keys(facetsData.tipe_file)
        : [];

    return {
        jenjang: facetJenjang.length ? facetJenjang : unique(booksData.map((book) => book.jenjang)),
        tipe: facetTipe.length ? facetTipe : unique(booksData.map((book) => book.tipe_file)),
    };
}

export function getBooksSnapshot() {
    return booksData;
}

// ── Recent pencarian & recent buku (kecil, aman dihapus) ──────────────────────
export function getRecentQueries() {
    try {
        const parsed = JSON.parse(safeGet(RECENT_QUERY_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string').slice(0, MAX_RECENT) : [];
    } catch {
        return [];
    }
}

export function addRecentQuery(query) {
    const value = String(query || '').trim();
    if (value.length < 2) return;

    const list = getRecentQueries().filter((item) => item.toLowerCase() !== value.toLowerCase());
    safeSet(RECENT_QUERY_KEY, JSON.stringify([value, ...list].slice(0, MAX_RECENT)));
}

export function clearRecentQueries() {
    safeRemove(RECENT_QUERY_KEY);
}

export function getRecentBooks() {
    try {
        const parsed = JSON.parse(safeGet(RECENT_BOOK_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter((item) => item && item.id).slice(0, MAX_RECENT) : [];
    } catch {
        return [];
    }
}

export function addRecentBook(book) {
    if (!book?.id) return;

    const slim = {
        id: book.id,
        judul: book.judul,
        image: book.image,
        tipe_file: book.tipe_file,
        jenis_buku: book.jenis_buku,
        jenjang: book.jenjang,
    };

    const list = getRecentBooks().filter((item) => String(item.id) !== String(slim.id));
    safeSet(RECENT_BOOK_KEY, JSON.stringify([slim, ...list].slice(0, MAX_RECENT)));
}

export function clearRecentBooks() {
    safeRemove(RECENT_BOOK_KEY);
}

// ── Resolusi slug ────────────────────────────────────────────────────────────
// Data /katalog/books hanya berisi id (tanpa slug), sedangkan URL detail
// memakai slug. Slug diambil sekali dari API detail lama (mendukung ?id=)
// lalu disimpan di memori + localStorage supaya klik berikutnya instan.

let slugMap = null;
const slugRequests = new Map();

function readSlugMap() {
    if (slugMap) return slugMap;
    try {
        slugMap = JSON.parse(safeGet(SLUG_MAP_KEY)) || {};
    } catch {
        slugMap = {};
    }
    return slugMap;
}

/**
 * Ambil slug buku berdasarkan id.
 * @param {string|number} id
 * @returns {Promise<string|null>}
 */
export function resolveBookSlug(id) {
    if (!id) return Promise.resolve(null);

    const map = readSlugMap();
    if (map[id]) return Promise.resolve(map[id]);
    if (slugRequests.has(id)) return slugRequests.get(id);

    const request = fetch(`${API_DETAIL}?id=${encodeURIComponent(id)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
            const slug = data?.results?.slug;
            if (slug) {
                map[id] = slug;
                safeSet(SLUG_MAP_KEY, JSON.stringify(map));
            }
            return slug || null;
        })
        .catch(() => null)
        .finally(() => slugRequests.delete(id));

    slugRequests.set(id, request);
    return request;
}

// ── Highlight ────────────────────────────────────────────────────────────────
/**
 * Pisahkan teks jadi bagian yang cocok / tidak cocok dengan kata kunci.
 * @returns {{ text: string, match: boolean }[]}
 */
export function highlightMatch(text, query) {
    if (!text || !query || !String(query).trim()) return [{ text, match: false }];

    const terms = String(query)
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter((term) => term.length > 1 || /\d/.test(term));

    if (terms.length === 0) return [{ text, match: false }];

    const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

    return String(text)
        .split(regex)
        .filter((part) => part !== '')
        .map((part) => ({
            text: part,
            match: terms.some((term) => part.toLowerCase().includes(term)),
        }));
}

/** Render teks dengan <mark> pada bagian yang cocok. */
export function HighlightedText({ text, query }) {
    const parts = highlightMatch(text, query);
    return (
        <>
            {parts.map((part, index) =>
                part.match ? (
                    <mark key={index} className="ms-highlight">{part.text}</mark>
                ) : (
                    <span key={index}>{part.text}</span>
                )
            )}
        </>
    );
}
